import { useEffect, useRef, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import { ChatEntryPoint } from '@atlaskit/rovo-triggers/chat-entry-point';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';
import {
	getLocalStorageItemAsString,
	setLocalStorageItem,
} from '@atlassian/search-common/local-storage';

import { useAreLLMPoweredFeaturesEnabled } from '../entitlement-checks';
import { useSearchPageProactiveAiCohort } from '../proactive-ai-experiment';
import {
	useSharedIsIntentClassified,
	useSharedIsSainAnswerVisible,
} from '../smart-answers-intent-store';
import { useSearchQuery } from '../store/search';

/** Source string we tag our own chat-new events with so we can distinguish them in our subscribe handler. */
export const AUTO_OPEN_SOURCE = 'search-page-auto-open-on-landing';

/** localStorage key tracking consecutive dismissals of an auto-opened chat. */
export const STORAGE_KEY_DISMISSAL_COUNT = 'search-page.auto-open-chat-dismiss-count';

/** Once the dismissal counter reaches this, suppression becomes sticky. */
export const AUTO_OPEN_MAX_DISMISSALS = 2;

/**
 * Engagement threshold as a fraction of the *scrollable distance*
 * (`scrollHeight - clientHeight`), matching the floating Rovo chat input's
 * `FpsFloatingContainer` reveal model so both experiences trigger at the same
 * relative scroll position. (Previously this was a fraction of the viewport
 * height, which made the mini-modal fire far more eagerly than the floating
 * input at the same configured value.)
 */
export const SCROLL_THRESHOLD_FRACTION = 0.1;
/** Absolute minimum scroll (px) before we ever auto-open, regardless of fraction. */
export const SCROLL_THRESHOLD_MIN_PX = 40;

const readDismissalCount = (): number => {
	try {
		const raw = getLocalStorageItemAsString(STORAGE_KEY_DISMISSAL_COUNT);
		const parsed = raw == null ? 0 : Number.parseInt(raw, 10);
		return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
	} catch {
		return 0;
	}
};

const writeDismissalCount = (count: number): void => {
	try {
		setLocalStorageItem(STORAGE_KEY_DISMISSAL_COUNT, String(count));
	} catch {
		/* Storage may be unavailable (incognito etc.) — silently no-op. */
	}
};

/**
 * Computes the engagement threshold in px for a given scroll-source's
 * scrollable distance, as `max(scrollableDistance * FRACTION, MIN_PX)`.
 *
 * `scrollableDistance` is `scrollHeight - clientHeight` (the total distance the
 * surface can scroll), matching the floating input's `FpsFloatingContainer`
 * model. When there is nothing to scroll the threshold collapses to the min
 * floor, but callers additionally require `scrollableDistance > 0` before
 * opening so a non-scrollable page never auto-opens.
 */
const computeThresholdPx = (scrollableDistance: number): number => {
	return Math.max(scrollableDistance * SCROLL_THRESHOLD_FRACTION, SCROLL_THRESHOLD_MIN_PX);
};

/**
 * Returns true if the given scroll source has moved past the engagement
 * threshold, measured as a fraction of its scrollable distance. `null` source
 * means "window/document". A source with no scrollable distance never passes.
 */
const hasSourceScrolledPastThreshold = (sourceElement: HTMLElement | null): boolean => {
	if (sourceElement) {
		const scrollableDistance = sourceElement.scrollHeight - sourceElement.clientHeight;
		if (scrollableDistance <= 0) {
			return false;
		}
		return sourceElement.scrollTop >= computeThresholdPx(scrollableDistance);
	}
	const docEl = document.scrollingElement || document.documentElement;
	const docScrollHeight = docEl ? docEl.scrollHeight : 0;
	const scrollableDistance = docScrollHeight - window.innerHeight;
	if (scrollableDistance <= 0) {
		return false;
	}
	const docScrollTop = docEl ? docEl.scrollTop : 0;
	const thresholdPx = computeThresholdPx(scrollableDistance);
	return window.scrollY >= thresholdPx || docScrollTop >= thresholdPx;
};

/**
 * Auto-opens the Ask Rovo mini-modal once per mount when the user lands on
 * the search page and engages (scrolls), unless SAIN will render an answer
 * for the query.
 *
 * The `auto_open_chat_modal` variant of the
 * `search_page_proactive_ai_multi_variant` Statsig experiment. This
 * controller is always mounted but only runs its auto-open behaviour for
 * that cohort. It reads the resolved cohort via the shared
 * `useSearchPageProactiveAiCohort()` hook and never fires the experiment
 * exposure itself — exposure is owned by AutoProactiveAiExperimentExposure
 * (mounted once in SearchPageBody) so all variant legs share a single
 * exposure on non-empty-term landing.
 *
 * Open criteria (all must be true): non-empty query, LLM enabled,
 * mini-modal subscriber mounted, SAIN classifier finished, SAIN won't
 * render an answer, user has scrolled past threshold, no other chat
 * currently open, dismissal count below cap.
 *
 * Dismissal counter (localStorage, per browser origin): incremented every
 * time a mini-modal chat is closed (regardless of who opened it). No reset
 * path — the counter grows monotonically until it hits the sticky cap,
 * after which auto-open is suppressed forever for that user.
 *
 * Renders nothing — pure side-effect controller.
 */
export const AutoOpenRovoChatOnLanding = (): null => {
	const [searchQuery] = useSearchQuery();
	const isIntentClassified = useSharedIsIntentClassified();
	const isSainAnswerVisible = useSharedIsSainAnswerVisible();
	const isLLMEnabled = useAreLLMPoweredFeaturesEnabled();
	const { isMiniModalAvailable, mode } = useRovoExperienceAPIStore();

	const publishRovo = usePublish('ai-mate');
	const hasOpenedRef = useRef(false);
	const [hasScrolledPastThreshold, setHasScrolledPastThreshold] = useState(false);

	const isAnyChatOpenRef = useRef(false);
	// Tracks whether the currently-open mini-modal was opened by THIS
	// controller's auto-open (vs the user opening a chat themselves). Only
	// auto-opened mini-modal closes should count toward the dismissal cap —
	// user-initiated opens & closes are deliberate UX and must not penalise
	// future auto-opens.
	const isCurrentChatOursRef = useRef(false);
	// Set when we auto-collapse our own mini-modal because the query changed.
	// The mode-watcher reads this so the resulting close transition is NOT
	// counted as a user dismissal (auto-collapse on a new search is not a
	// rejection of the feature). A ref (not state) so the value is visible
	// synchronously to the close-transition handler.
	const isAutoCollapsingRef = useRef(false);
	// Set during render when the query changes while our auto-opened mini-modal
	// is open, and consumed by the auto-collapse effect below to publish
	// `chat-close`. Ref + state: the ref carries intent, the state schedules the
	// effect deterministically for that specific query change.
	const shouldAutoCollapseRef = useRef(false);
	const [autoCollapseToken, setAutoCollapseToken] = useState(0);

	// Reset per-query engagement signals during render (not in an effect) so the
	// auto-open effect below cannot read a stale `hasScrolledPastThreshold=true`
	// from the previous query before a post-commit reset would have run.
	// Normalised via `?? ''` so `undefined` and `''` are treated the same.
	const normalisedQuery = searchQuery ?? '';
	const previousSearchQueryRef = useRef<string>(normalisedQuery);
	if (previousSearchQueryRef.current !== normalisedQuery) {
		previousSearchQueryRef.current = normalisedQuery;
		hasOpenedRef.current = false;
		if (hasScrolledPastThreshold) {
			setHasScrolledPastThreshold(false);
		}
		// Auto-collapse on new query: if OUR auto-opened mini-modal is still
		// open when the user runs a new search, schedule a `chat-close`. We
		// only collapse chats we opened ourselves (`isCurrentChatOursRef`) and
		// only the mini-modal (`mode`) — never the user's own chats or the
		// sidebar. The actual publish happens in an effect (no side effects
		// during render).
		if (isAnyChatOpenRef.current && isCurrentChatOursRef.current && mode === 'mini-modal') {
			shouldAutoCollapseRef.current = true;
			setAutoCollapseToken((token) => token + 1);
		}
	}

	// Read-only consumption of the shared proactive-AI experiment cohort. The
	// exposure is owned by AutoProactiveAiExperimentExposure (mounted once in
	// SearchPageBody) so this controller never fires an exposure itself.
	const cohort = useSearchPageProactiveAiCohort();
	const isAutoOpenChatModalCohort = cohort === 'auto_open_chat_modal';

	// Watch scroll via document capture-phase listener (scroll events do not
	// bubble — capture catches them from every scroll container in the page).
	useEffect(() => {
		if (typeof window === 'undefined' || hasScrolledPastThreshold) {
			return;
		}

		const onScrollEvent = (event: Event) => {
			const target = event.target;
			const sourceElement =
				target instanceof HTMLElement &&
				target !== document.documentElement &&
				target !== document.body
					? target
					: null;
			if (hasSourceScrolledPastThreshold(sourceElement)) {
				setHasScrolledPastThreshold(true);
			}
		};

		// Cover the case where the browser restored a past-threshold scroll
		// position on load (no scroll event will fire there). We only check
		// the window/document scroller here (`null` source) — element scroll
		// containers are handled by the capture-phase listener once they emit
		// a scroll event.
		if (hasSourceScrolledPastThreshold(null)) {
			setHasScrolledPastThreshold(true);
			return;
		}

		const unbindDocument = bindAll(document, [
			{
				type: 'scroll',
				listener: onScrollEvent,
				options: { capture: true, passive: true } as AddEventListenerOptions,
			},
		]);
		const unbindWindow = bindAll(window, [
			{
				type: 'scroll',
				listener: onScrollEvent,
				options: { passive: true } as AddEventListenerOptions,
			},
		]);
		return () => {
			unbindDocument();
			unbindWindow();
		};
	}, [hasScrolledPastThreshold]);

	// Watch the rovo-experience-api store's `mode` transitions to track chat
	// open/close lifecycle. Production code does not publish `chat-close`
	// pubsub events; the Rovo chat module manages close state internally via
	// this store (mode → undefined).
	//
	// Dismissal counter rule: increment ONLY when an auto-opened mini-modal
	// is closed. User-initiated opens & subsequent closes do not count.
	// No reset path — the counter only ever grows until it reaches the
	// sticky cap, at which point auto-open is suppressed forever for this
	// user (per-origin localStorage).
	// Sidebar closes never count either — sidebar is a deliberate UX.
	const previousModeRef = useRef<typeof mode>(undefined);
	useEffect(() => {
		const prevMode = previousModeRef.current;
		const currMode = mode;
		previousModeRef.current = currMode;

		// Close transition: any → undefined
		if (prevMode !== undefined && currMode === undefined) {
			isAnyChatOpenRef.current = false;
			const wasOurs = isCurrentChatOursRef.current;
			const wasAutoCollapse = isAutoCollapsingRef.current;
			isCurrentChatOursRef.current = false;
			isAutoCollapsingRef.current = false;
			// Auto-collapse on a new query is not a user dismissal — never count
			// it toward the sticky dismissal cap. Only genuine closes of our
			// auto-opened mini-modal count.
			if (prevMode === 'mini-modal' && wasOurs && !wasAutoCollapse) {
				const current = readDismissalCount();
				if (current < AUTO_OPEN_MAX_DISMISSALS) {
					writeDismissalCount(current + 1);
				}
			}
			return;
		}

		// Open transition: undefined → any
		if (prevMode === undefined && currMode !== undefined) {
			isAnyChatOpenRef.current = true;
		}
	}, [mode]);

	// Auto-collapse effect. When a new query flags `shouldAutoCollapseRef`
	// (our auto-opened mini-modal was open at the time of the search), publish
	// `chat-close` to collapse it. `isAutoCollapsingRef` is set first so the
	// mode-watcher's close transition does not count this as a dismissal. The
	// mini-modal can re-open later for the new query once the user re-engages
	// (the query-change block above already reset the open/scroll gates).
	useEffect(() => {
		if (!shouldAutoCollapseRef.current) {
			return;
		}
		shouldAutoCollapseRef.current = false;
		// Re-check: the chat may have already been closed between the render
		// that scheduled this and now.
		if (!isAnyChatOpenRef.current || !isCurrentChatOursRef.current) {
			return;
		}
		isAutoCollapsingRef.current = true;
		publishRovo({
			type: 'chat-close',
			source: AUTO_OPEN_SOURCE,
			data: {},
		});
	}, [autoCollapseToken, publishRovo]);

	// Auto-open effect. Runs after every gate read; all guards short-circuit
	// quickly so cheap to re-evaluate. Behaviour is enabled only for the
	// `auto_open_chat_modal` cohort; exposure is fired separately at the
	// non-empty-term landing point above (so all variant legs are exposed).
	useEffect(() => {
		if (
			!isAutoOpenChatModalCohort ||
			hasOpenedRef.current ||
			!isLLMEnabled ||
			!isMiniModalAvailable ||
			normalisedQuery.trim().length === 0 ||
			!isIntentClassified ||
			isSainAnswerVisible ||
			!hasScrolledPastThreshold ||
			isAnyChatOpenRef.current ||
			readDismissalCount() >= AUTO_OPEN_MAX_DISMISSALS
		) {
			return;
		}

		hasOpenedRef.current = true;
		// Mark BEFORE publish so the mode-watcher's open transition
		// (undefined → 'mini-modal') sees us as the owner.
		isCurrentChatOursRef.current = true;
		publishRovo({
			type: 'chat-new',
			source: AUTO_OPEN_SOURCE,
			openChat: true,
			openChatMode: 'mini-modal',
			data: {
				dialogues: [],
				chatEntryPoint: ChatEntryPoint.SEARCH_PAGE_AUTO_OPEN_CHAT,
			},
		});
	}, [
		isAutoOpenChatModalCohort,
		isLLMEnabled,
		isMiniModalAvailable,
		normalisedQuery,
		isIntentClassified,
		isSainAnswerVisible,
		hasScrolledPastThreshold,
		publishRovo,
	]);

	return null;
};
